import type { ReactNode } from 'react'
import { ResponsiveNavigation } from '@marque/ui'
import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { StatusGroup } from '../api'

export type Page = 'overview' | 'pipeline' | 'health' | 'releases' | 'landscape' | 'cluster' | 'gitops' | 'troubleshoot' | 'status' | 'secrets' | 'jobs' | 'tokens' | 'images' | 'branches' | 'boards'

interface NavItem {
    id: Page
    label: string
    section: string
    icon: ReactNode
}

const navItems: NavItem[] = [
    { id: 'boards', label: 'Delivery boards', section: 'Work Items', icon: <span aria-hidden="true">▤</span> },
    {
        id: 'overview',
        label: 'Overview',
        section: 'Release',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="8" r="6" />
                <circle cx="8" cy="8" r="2" fill="currentColor" stroke="none" />
            </svg>
        ),
    },
    {
        id: 'pipeline',
        label: 'Pipeline',
        section: 'Release',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="4" width="3" height="8" rx="1" />
                <rect x="6.5" y="2" width="3" height="12" rx="1" />
                <rect x="12" y="5" width="3" height="6" rx="1" />
            </svg>
        ),
    },
    {
        id: 'health',
        label: 'Health',
        section: 'Release',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M1 8h2l2-5 2 10 2-6 1.5 3H15" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        id: 'releases',
        label: 'Work Items',
        section: 'Work Items',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 4h9M2 8h7M2 12h5" strokeLinecap="round" />
                <circle cx="13" cy="12" r="2" />
                <path d="M13 10V5l-2 2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        id: 'landscape',
        label: 'Landscape',
        section: 'Landscape',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="3.5" cy="8" r="2" />
                <circle cx="8" cy="8" r="2" />
                <circle cx="12.5" cy="8" r="2" />
                <path d="M5.5 8h1M10 8h1" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        id: 'gitops',
        label: 'GitOps',
        section: 'Infrastructure',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="4" cy="4" r="2" />
                <circle cx="12" cy="4" r="2" />
                <circle cx="8" cy="12" r="2" />
                <path d="M4 6v1.5a2.5 2.5 0 0 0 2.5 2.5h3A2.5 2.5 0 0 0 12 7.5V6" strokeLinecap="round" />
                <line x1="8" y1="9" x2="8" y2="10" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        id: 'cluster',
        label: 'Cluster',
        section: 'Infrastructure',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="6" height="5" rx="1" />
                <rect x="9" y="1" width="6" height="5" rx="1" />
                <rect x="1" y="10" width="6" height="5" rx="1" />
                <rect x="9" y="10" width="6" height="5" rx="1" />
                <path d="M4 6v2h8V6" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="8" y1="8" x2="8" y2="10" />
            </svg>
        ),
    },
    {
        id: 'status',
        label: 'Infra Status',
        section: 'Infrastructure',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="4"  cy="8" r="1.5" />
                <circle cx="8"  cy="8" r="1.5" />
                <circle cx="12" cy="8" r="1.5" />
                <path d="M1 5h14M1 11h14" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        id: 'troubleshoot',
        label: 'Troubleshoot',
        section: 'Infrastructure',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="8" r="6" />
                <line x1="8" y1="5" x2="8" y2="8.5" strokeLinecap="round" />
                <circle cx="8" cy="11" r="0.5" fill="currentColor" stroke="none" />
            </svg>
        ),
    },
    {
        id: 'secrets',
        label: 'Secrets',
        section: 'Infrastructure',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="7" width="10" height="7" rx="1.5" />
                <path d="M5 7V5a3 3 0 0 1 6 0v2" strokeLinecap="round" />
                <circle cx="8" cy="10.5" r="1" fill="currentColor" stroke="none" />
            </svg>
        ),
    },
    {
        id: 'jobs',
        label: 'Background Jobs',
        section: 'Infrastructure',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="8" r="6" />
                <path d="M8 5v3l2 1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        id: 'tokens',
        label: 'Dev Tokens',
        section: 'Developer',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="5" width="10" height="6" rx="1.5" />
                <circle cx="5" cy="8" r="1" fill="currentColor" stroke="none" />
                <path d="M11 6.5l3-1.5v6l-3-1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        id: 'images',
        label: 'Images',
        section: 'Developer',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1" y="1" width="14" height="14" rx="2" />
                <circle cx="5" cy="5.5" r="1.5" />
                <path d="M1 11l4-4 3 3 2-2 5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        id: 'branches',
        label: 'Branches',
        section: 'Developer',
        icon: (
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="4" cy="4" r="1.5" />
                <circle cx="4" cy="12" r="1.5" />
                <circle cx="12" cy="4" r="1.5" />
                <path d="M4 5.5v5M5.5 4h3.5a1 1 0 0 1 1 1v1.5" strokeLinecap="round" />
            </svg>
        ),
    },
]

const sections = ['Release', 'Work Items', 'Landscape', 'Infrastructure', 'Developer']

// ── Nav status dot helpers ────────────────────────────────────────────────────

type NavDotLevel = 'red' | 'amber' | null

function worstDotInGroups(groups: StatusGroup[], groupIds: string[]): NavDotLevel {
    const relevant = groups.filter(g => groupIds.includes(g.id))
    let hasError = false
    let hasWarning = false
    for (const g of relevant) {
        for (const d of g.dots) {
            if (d.status === 'Error') hasError = true
            if (d.status === 'Warning') hasWarning = true
        }
    }
    return hasError ? 'red' : hasWarning ? 'amber' : null
}

function NavDot({ level }: { level: NavDotLevel }) {
    if (!level) return null
    const cls = level === 'red' ? 'bg-red-500' : 'bg-amber-400'
    return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cls}`} />
}

// ── Layout ────────────────────────────────────────────────────────────────────

interface LayoutProps {
    page: Page
    onNavigate: (page: Page) => void
    children: ReactNode
}

export function Layout({ page, onNavigate, children }: LayoutProps) {
    // Fetch infra status for nav dots — React Query caches this so other pages
    // that also fetch getInfraStatus() share the same result without a second request.
    const { data: infraStatus } = useQuery({
        queryKey: ['infraStatus'],
        queryFn: releaseApi.getInfraStatus,
        refetchInterval: 30_000,
        staleTime: 20_000,
    })

    const groups = infraStatus?.groups ?? []

    const { data: secretsData } = useQuery({
        queryKey: ['secret-expiry'],
        queryFn: releaseApi.getSecretExpiry,
        refetchInterval: 5 * 60_000,
        staleTime: 4 * 60_000,
    })

    const secretsDot: NavDotLevel = (() => {
        if (!secretsData) return null
        const now = Date.now()
        const hasExpired  = secretsData.secrets.some(s => s.enabled && s.expiresOn && new Date(s.expiresOn).getTime() < now)
        const hasCritical = secretsData.secrets.some(s => s.enabled && s.expiresOn && !hasExpired && (new Date(s.expiresOn).getTime() - now) < 7 * 86_400_000)
        const hasWarning  = secretsData.secrets.some(s => s.enabled && s.expiresOn && (new Date(s.expiresOn).getTime() - now) < 30 * 86_400_000)
        if (hasExpired || hasCritical) return 'red'
        if (hasWarning) return 'amber'
        return null
    })()

    const navDots: Partial<Record<Page, NavDotLevel>> = {
        gitops: worstDotInGroups(groups, ['gitops']),
        cluster: worstDotInGroups(groups, ['cluster']),
        // Infra Status shows worst of all groups combined
        status: worstDotInGroups(groups, groups.map(g => g.id)),
        // Troubleshoot watches gitops group (ESO + ArgoCD errors)
        troubleshoot: worstDotInGroups(groups, ['gitops']),
        secrets: secretsDot,
    }

    return (
        <div className="min-h-dvh bg-slate-950 text-white flex flex-col md:flex-row">
            <ResponsiveNavigation title="Marque Ops" dark>
            <aside className="w-44 shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col">
                <div className="px-4 py-5 border-b border-slate-800">
                    <div className="text-sm font-bold tracking-tight text-white">Marque</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 font-medium tracking-wide">Ops Portal</div>
                </div>

                <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5">
                    {sections.map(section => (
                        <div key={section}>
                            <span className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 block">
                                {section}
                            </span>
                            {navItems.filter(i => i.section === section).map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => onNavigate(item.id)}
                                    className={[
                                        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors text-left w-full',
                                        page === item.id
                                            ? 'bg-slate-800 text-white'
                                            : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200',
                                    ].join(' ')}
                                >
                                    {item.icon}
                                    <span className="flex-1 min-w-0 truncate">{item.label}</span>
                                    <NavDot level={navDots[item.id] ?? null} />
                                </button>
                            ))}
                        </div>
                    ))}
                </nav>

                {import.meta.env.VITE_APP_VERSION && (
                    <div className="px-4 py-2 text-[10px] text-slate-700 font-mono select-all">
                        {import.meta.env.VITE_APP_VERSION.slice(0, 7)}
                    </div>
                )}

                <div className="px-2 py-3 border-t border-slate-800">
                    <button
                        onClick={() => { window.location.href = '/signout' }}
                        className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 transition-colors w-full"
                    >
                        <svg className="h-4 w-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M6 3H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3" strokeLinecap="round" />
                            <path d="M10 5l3 3-3 3M13 8H6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Sign out</span>
                    </button>
                </div>
            </aside>
            </ResponsiveNavigation>

            <main className="flex-1 flex flex-col min-w-0 overflow-x-auto">
                {children}
            </main>
        </div>
    )
}
